import type {
  AdaptedCommunicationPlanFeedEntry,
  AdaptedCaseCommunicationScheduleEvent,
} from "../../../../../../../../api/kehrblatt/case-communications"
import type { ContactOrderStep } from "../../../../../../../../api/kehrblatt/contact-orders"
import type {
  CommunicationPlanStatusCode,
  ContactOrderPlanRow,
  ContactOrderStepPatchMap,
} from "../../../_logic/communication-plan-table-contract"
import { buildContactOrderPlanRows, buildWorkOrderPlanningRows } from "./tableModel"
import { applyCommunicationContactEffectiveAt } from "./contactPresentation"
import { buildCommunicationPlanStatusPresentation } from "./statusPresentation"
import { buildCommunicationWorkPresentation } from "../../../_logic/work-presentation"

export type CommunicationPlanFeedRowsInput = {
  entries: AdaptedCommunicationPlanFeedEntry[]
  workOrders?: WorkOrder[]
  properties?: Property[]
  scheduleEvents?: AdaptedCaseCommunicationScheduleEvent[]
  customerById?: Record<string, Customer>
  contactPointById?: Record<string, ContactPoint>
  stepPatches?: ContactOrderStepPatchMap
  now?: number
}

const patchedStep = (step: ContactOrderStep, patches: ContactOrderStepPatchMap) => {
  const patch = patches[step.id]

  return patch ? ({ ...step, ...patch } as ContactOrderStep) : step
}

type ContactStepFeedEntry = Extract<AdaptedCommunicationPlanFeedEntry, { kind: "contact_step" }>
type StandaloneCommunicationFeedEntry = Extract<
  AdaptedCommunicationPlanFeedEntry,
  { kind: "case_communication" }
>

type ContactStepRowInput = {
  entry: ContactStepFeedEntry
  workOrders: WorkOrder[]
  scheduleEvents: AdaptedCaseCommunicationScheduleEvent[]
  customerById: Record<string, Customer>
  contactPointById: Record<string, ContactPoint>
  stepPatches: ContactOrderStepPatchMap
  now: number
}

type StandaloneCommunicationRowInput = {
  entry: StandaloneCommunicationFeedEntry
  workOrders: WorkOrder[]
  properties: Property[]
  scheduleEvents: AdaptedCaseCommunicationScheduleEvent[]
  customerById: Record<string, Customer>
  contactPointById: Record<string, ContactPoint>
  now: number
}

const contactStepRow = ({
  entry,
  workOrders,
  scheduleEvents,
  customerById,
  contactPointById,
  stepPatches,
  now,
}: ContactStepRowInput): ContactOrderPlanRow | null => {
  const step = patchedStep(entry.step, stepPatches)
  const order = {
    ...entry.order,
    steps: (entry.order.steps ?? []).map((candidate) =>
      candidate.id === step.id ? step : patchedStep(candidate, stepPatches),
    ),
  }
  const base = buildContactOrderPlanRows({
    order,
    workOrders,
    scheduleEvents,
    customerById,
    contactPointById,
    caseCommunications: entry.communication ? [entry.communication] : [],
    stepPatches,
    now,
  }).find((row) => row.step?.id === step.id)

  if (!base) return null

  const row: ContactOrderPlanRow = {
    ...base,
    id: entry.entryKey,
    kind: "contact_step",
    source: "planned",
    step,
    stepActionContext: { order, step },
    caseCommunication: entry.communication,
    communication: entry.communication?.communication ?? null,
    workOrderId: entry.extension?.work_order?.id ?? base.workOrderId ?? order.work_order_id ?? null,
    work: buildCommunicationWorkPresentation(entry.extension),
    contact: applyCommunicationContactEffectiveAt(base.contact, entry.effectiveAt),
  }
  const status = buildCommunicationPlanStatusPresentation({
    entry: { ...entry, order, step },
    existingHint: row.hint,
    now,
  })

  return {
    ...row,
    status: status.definition.value as CommunicationPlanStatusCode,
    statusLabel: status.definition.label,
    statusDefinition: status.definition,
    statusSubLabel: status.subLabel,
    statusTooltip: status.tooltipHtml,
    executionDefinition: status.definition,
    executionTooltip: status.tooltipHtml,
    hint: status.hint,
    hintText: status.hint?.label ?? null,
    hintTooltip: status.hint?.tooltipHtml ?? status.hint?.subLabel ?? null,
  }
}

const standaloneCommunicationRow = ({
  entry,
  workOrders,
  properties,
  scheduleEvents,
  customerById,
  contactPointById,
  now,
}: StandaloneCommunicationRowInput): ContactOrderPlanRow | null => {
  const base = buildWorkOrderPlanningRows({
    contactOrders: [],
    workOrders,
    properties,
    scheduleEvents,
    customerById,
    contactPointById,
    caseCommunications: [entry.communication],
    stepPatches: {},
    now,
  })[0]

  if (!base) return null

  const row: ContactOrderPlanRow = {
    ...base,
    id: entry.entryKey,
    kind: "case_communication",
    source: "case_communication",
    step: null,
    stepActionContext: null,
    caseCommunication: entry.communication,
    communication: entry.communication.communication,
    workOrderId: entry.extension?.work_order?.id ?? base.workOrderId ?? null,
    work: buildCommunicationWorkPresentation(entry.extension),
    contact: applyCommunicationContactEffectiveAt(base.contact, entry.effectiveAt),
  }
  const status = buildCommunicationPlanStatusPresentation({ entry, existingHint: row.hint, now })

  return {
    ...row,
    status: status.definition.value as CommunicationPlanStatusCode,
    statusLabel: status.definition.label,
    statusDefinition: status.definition,
    statusSubLabel: status.subLabel,
    statusTooltip: status.tooltipHtml,
    executionDefinition: status.definition,
    executionTooltip: status.tooltipHtml,
    hint: status.hint,
    hintText: status.hint?.label ?? null,
    hintTooltip: status.hint?.tooltipHtml ?? status.hint?.subLabel ?? null,
  }
}

/** Builds exactly one table row for every valid table-projection feed entry. */
export const buildCommunicationPlanFeedRows = ({
  entries,
  workOrders = [],
  properties = [],
  scheduleEvents = [],
  customerById = {},
  contactPointById = {},
  stepPatches = {},
  now = Date.now(),
}: CommunicationPlanFeedRowsInput): ContactOrderPlanRow[] =>
  entries.flatMap((entry) => {
    const row =
      entry.kind === "contact_step"
        ? contactStepRow({
            entry,
            workOrders,
            scheduleEvents,
            customerById,
            contactPointById,
            stepPatches,
            now,
          })
        : standaloneCommunicationRow({
            entry,
            workOrders,
            properties,
            scheduleEvents,
            customerById,
            contactPointById,
            now,
          })

    return row ? [row] : []
  })
